import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { AddOnTextInput, Button, Divider, IconButton, Spinner, TextInput, Typography, Modal } from "@/components/atoms";
import { IntervalRow } from "@/components/molecules";
import { usePrompt } from "@/hooks/usePrompt";
import { useUpdateStandardPricingMutation } from "@/redux/apis/config/trip/cancellationFees";
import { StandardCancelFeeInterval } from "@/redux/slices/trip/types";
import { addNotification } from "@/utils";
import { modals } from "./fixtures";
import { StandardPricingFormData } from "./types";

export interface StandardCancelFeesEditorProps {
  basePrice: StandardCancelFeeInterval;
  intervals: Array<StandardCancelFeeInterval>;
}

export const StandardCancelFeesEditor = ({ basePrice, intervals }: StandardCancelFeesEditorProps) => {
  const navigate = useNavigate();
  const [updateStandardPricing, { isLoading }] = useUpdateStandardPricingMutation();
  const [activeModal, setActiveModal] = useState<"exit" | "delete" | "error" | null>(null);
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { isDirty },
  } = useForm<StandardPricingFormData>({
    defaultValues: {
      basePrice,
      intervals,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "intervals",
  });

  usePrompt({ when: isDirty && activeModal !== "exit", message: "Changes will not be saved. Do you want to proceed?" });

  const handleAddRow = () => {
    append({
      rangeTo: fields.length > 0 ? fields[fields.length - 1].rangeTo + 1 : basePrice.rangeTo + 1,
      rate: 0,
    });
  };

  const handleConfirmDelete = (i: number) => {
    setPendingDelete(i);
    setActiveModal("delete");
  };

  const onSubmit = handleSubmit((data) => {
    updateStandardPricing({
      base_interval: {
        range_to: data.basePrice.rangeTo,
        rate: data.basePrice.rate,
      },
      intervals: data.intervals.map((i) => ({
        range_to: i.rangeTo,
        rate: i.rate,
      })),
    })
      .unwrap()
      .then(() => {
        addNotification("info", "Update Successful", "Successfully Updated Standard Cancellation Fees");
        navigate(-1);
      })
      .catch(() => setActiveModal("error"));
  });

  const renderRows = () => {
    return fields.map((interval, i) => {
      return (
        <IntervalRow
          key={interval.id}
          label={`Interval ${i + 1}`}
          renderRange={() => (
            <div className="flex flex-1 items-center gap-x-4">
              <Typography variant="small" className="leading-6 text-neutral-dark-gray">
                Maximum Hours
              </Typography>
              <div>
                <AddOnTextInput
                  endAddOn="Hours"
                  type="number"
                  className="w-[125px] [&_input]:text-right"
                  step="1"
                  min="0"
                  max="999"
                  required
                  {...register(`intervals.${i}.rangeTo`)}
                />
              </div>
            </div>
          )}
          renderRate={() => (
            <div className="flex items-center gap-x-2">
              <div className="flex items-center gap-x-4">
                <Typography variant="small" className="leading-6 text-neutral-dark-gray">
                  Percentage rate
                </Typography>
                <div>
                  <AddOnTextInput
                    endAddOn="%"
                    type="number"
                    step="1"
                    min="0"
                    max="9999"
                    className="w-[110px] [&_input]:text-right"
                    required
                    {...register(`intervals.${i}.rate`)}
                  />
                </div>
              </div>
              <IconButton
                onClick={() => handleConfirmDelete(i)}
                iconName="Trash"
                variant="tertiary"
                className="!text-danger hover:bg-danger-light"
              />
            </div>
          )}
        />
      );
    });
  };

  const renderModal = () => {
    if (!activeModal) return;

    return (
      <Modal
        open={true}
        title={modals[activeModal].title}
        description={modals[activeModal].description}
        onClose={() => setActiveModal(null)}
        renderButtons={renderModalButtons}
      />
    );
  };

  const renderModalButtons = () => {
    const handleDeleteRow = () => {
      if (pendingDelete === null) return;
      remove(pendingDelete);
      setActiveModal(null);
    };

    switch (activeModal) {
      case "delete":
        return (
          <>
            <Button variant="secondary" onClick={() => setActiveModal(null)} className="px-8">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleDeleteRow} className="bg-danger px-8 hover:bg-danger-dark">
              Delete
            </Button>
          </>
        );
      case "exit":
        return (
          <>
            <Button variant="secondary" onClick={() => setActiveModal(null)} className="px-8">
              Cancel
            </Button>
            <Button variant="primary" onClick={() => navigate("../")} className="bg-primary px-8 hover:bg-primary-dark">
              Discard Changes
            </Button>
          </>
        );
      default:
        return (
          <Button variant="primary" onClick={() => setActiveModal(null)} className="px-8">
            Back
          </Button>
        );
    }
  };

  return (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex-1">
          <Typography variant="h3" className="leading-8">
            Standard Cancellation Fees
          </Typography>
          <Typography className="text-neutral-dark-gray">Set the cancellation fees for your business.</Typography>
        </div>
        <div className="flex-shrink-0 ">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => {
                isDirty ? setActiveModal("exit") : navigate("../");
              }}
              variant="secondary"
              className="px-6"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-primary px-6 hover:bg-primary-dark"
              form="standard-cancellation-fees-form"
              disabled={!isDirty}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
      <div className="relative mt-4">
        {renderModal()}
        {isLoading && (
          <div className="absolute flex h-full w-full items-center justify-center bg-white/90">
            <Spinner className="h-8 w-8" />
          </div>
        )}
        <form onSubmit={onSubmit} id="standard-cancellation-fees-form">
          <IntervalRow
            label="Minimum Time"
            renderRange={() => (
              <div className="flex flex-1 items-center gap-x-4">
                <Typography variant="small" className="leading-6 text-neutral-dark-gray">
                  Range from
                </Typography>
                <div className="flex items-center gap-x-2">
                  <TextInput type="number" className="w-[46px] [&_input]:text-right" min="0" max="99999" disabled value="0" required />
                  <Typography variant="small" className="leading-6 text-neutral-dark-gray">
                    to
                  </Typography>
                  <AddOnTextInput
                    endAddOn="Hours"
                    type="number"
                    className="w-[125px] [&_input]:text-right"
                    step="1"
                    min="0"
                    max="999"
                    {...register("basePrice.rangeTo")}
                    required
                  />
                </div>
              </div>
            )}
            renderRate={() => (
              <div className="flex items-center gap-x-4">
                <Typography variant="small" className="leading-6 text-neutral-dark-gray">
                  Percentage rate
                </Typography>
                <div>
                  <AddOnTextInput
                    endAddOn="%"
                    type="number"
                    step="1"
                    min="0"
                    max="9999"
                    {...register("basePrice.rate")}
                    required
                    className="w-[110px] [&>input]:text-right"
                  />
                </div>
              </div>
            )}
            className="mt-6"
          />
          <Divider className="my-6" />
          <div className="space-y-4">{renderRows()}</div>
          <div className="mt-4">
            <Button
              variant="tertiary"
              startIcon="Add"
              className="w-full justify-center py-4 sm:bg-primary-light sm:text-primary"
              onClick={handleAddRow}
              size="xl"
            >
              Add New Interval
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};
